var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Enum_LiveStream_Status } from '@core/api';
import useWebSocket, { ReadyState } from '@core/hooks/useWebSocket';
import { updateVideoLiveStreamParams } from '@embed/helpers';
import { fetchOrCreateGuestId } from '@embed/store/session/helpers';
import { defaultApiHost } from '@src/helpers';
// vars shared between hook instances
// making sure socked does not reconnect
let wasConnected = false;
let heartbeatInterval = null;
let joinedTopics = {};
let ref = 0;
export const useLiveStreamingLite = (props) => {
    var _a, _b, _c;
    const { video, params } = props;
    const url = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        var _d;
        const baseUrl = (_d = params === null || params === void 0 ? void 0 : params.api_host) !== null && _d !== void 0 ? _d : defaultApiHost;
        const host = new URL(baseUrl).host;
        return `wss://${host}/socket/websocket?guest_id=${yield fetchOrCreateGuestId()}`;
    }), [params === null || params === void 0 ? void 0 : params.api_host]);
    const [liveStreamDetails, setLiveStreamDetails] = useState(() => ({}));
    const liveStreamId = useMemo(() => video === null || video === void 0 ? void 0 : video.live_stream_id, [video === null || video === void 0 ? void 0 : video.live_stream_id]);
    const topic = useMemo(() => (liveStreamId ? `live_stream_light:${liveStreamId}` : null), [liveStreamId]);
    const callbacks = useMemo(() => ({
        shouldReconnect: () => true,
        onError: () => {
            wasConnected = false;
            heartbeatInterval = null;
            joinedTopics = {};
        },
        onClose: () => {
            wasConnected = false;
            heartbeatInterval = null;
            joinedTopics = {};
        },
        onOpen: () => {
            wasConnected = true;
        },
        onMessage: (event) => __awaiter(void 0, void 0, void 0, function* () {
            const data = JSON.parse(event.data);
            const liveStreamId = data.topic.replace('live_stream_light:', '');
            switch (data.event) {
                case 'phx_reply':
                    switch (data.ref) {
                        case 'joinRef': {
                            setLiveStreamDetails((state) => (Object.assign(Object.assign({}, state), { [`${liveStreamId}`]: Object.assign(Object.assign({}, state[liveStreamId]), data.payload.response) })));
                        }
                    }
                    break;
                case 'start_live_stream':
                case 'complete_live_stream':
                case 'update_live_stream':
                case 'pause_live_stream':
                case 'resume_live_stream': {
                    setLiveStreamDetails((state) => (Object.assign(Object.assign({}, state), { [`${liveStreamId}`]: Object.assign(Object.assign({}, state[liveStreamId]), data.payload.live_stream_detail) })));
                    break;
                }
                default:
                    break;
            }
        }),
    }), []);
    // Note: To keep single websocket connection, reference to `url` promise and `connect` (3rd attribute)
    // params can't change. Dependency array inside useWebSocket consists of [url, connect]
    const { sendJsonMessage, readyState } = useWebSocket(url, Object.assign({ reconnectInterval: 5000, retryOnError: true, share: true }, callbacks), ((video === null || video === void 0 ? void 0 : video.live_stream_status) &&
        !!liveStreamId &&
        [
            Enum_LiveStream_Status.IDLE,
            Enum_LiveStream_Status.ACTIVE,
            Enum_LiveStream_Status.PAUSED,
        ].includes(video === null || video === void 0 ? void 0 : video.live_stream_status)) ||
        wasConnected);
    useEffect(() => {
        switch (readyState) {
            case ReadyState.OPEN: {
                // Join topic if not joined yet
                if ((video === null || video === void 0 ? void 0 : video.live_stream_status) &&
                    topic &&
                    !joinedTopics[topic] &&
                    [
                        Enum_LiveStream_Status.IDLE,
                        Enum_LiveStream_Status.ACTIVE,
                        Enum_LiveStream_Status.PAUSED,
                    ].includes(video === null || video === void 0 ? void 0 : video.live_stream_status)) {
                    joinedTopics[topic] = true;
                    sendJsonMessage({
                        topic: topic,
                        event: 'phx_join',
                        ref: 'joinRef',
                        payload: {},
                    });
                }
                // Set up heartbeats
                if (!heartbeatInterval) {
                    const heartbeat = () => {
                        sendJsonMessage({
                            topic: 'phoenix',
                            event: 'heartbeat',
                            payload: {},
                            ref: String(ref++),
                        });
                    };
                    heartbeatInterval = setInterval(heartbeat, 30000);
                }
                break;
            }
        }
    }, [
        readyState,
        sendJsonMessage,
        topic,
        liveStreamId,
        video === null || video === void 0 ? void 0 : video.live_stream_status,
    ]);
    const updatedParams = updateVideoLiveStreamParams(video, liveStreamDetails[(_a = video.live_stream_id) !== null && _a !== void 0 ? _a : '']);
    return {
        status: ((_c = liveStreamDetails[(_b = video.live_stream_id) !== null && _b !== void 0 ? _b : '']) === null || _c === void 0 ? void 0 : _c.status) || null,
        video: updatedParams
            ? Object.assign(Object.assign({}, video), updatedParams) : video,
    };
};
